import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Button from 'common/Button';
import RawHtml from 'common/RawHtml';
import dialogActions from 'core/dialogs/actions';
import sessionActions from 'core/session/actions';
import analyticsActions from 'core/analytics/actions';
import Spinner from 'common/Spinner';

import iconTimeout from 'icons/icon-time-out.svg';
import headerStyles from './styles/header';
import titleStyles from './styles/title';
import contentWrapperStyles from './styles/contentWrapper';
import dialogStyles from './styles/dialog';
import contentStyles from './styles/content';
import bodyStyles from './styles/body';
import footerStyles from './styles/footer';

const styles = (theme) => {
  const common = {
    body: bodyStyles(theme),
    content: contentStyles(theme),
    contentWrapper: contentWrapperStyles(theme),
    dialog: dialogStyles(theme),
    footer: footerStyles(theme),
    header: headerStyles(theme),
    title: titleStyles(theme),
  };
  return {
    ...common,
    header: {
      ...common.header,
      background: `url(${iconTimeout}) 50% 50% no-repeat`,
      backgroundSize: '61px 72px',
      paddingTop: 80,
    },
  };
};

export const SessionTimeoutBare = ({
  analyticsTimeout,
  analyticsTimeoutWarning,
  buttons,
  classes,
  close,
  commonClasses,
  getSession,
  logoutUrl,
  message,
  pageName,
  timerMessage,
  title,
}) => {
  const [time, setTime] = useState(60);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (time <= 0) {
        analyticsTimeout(pageName);
        close();
        window.location.assign(logoutUrl || '/oao/userauth');
      } else {
        setTime((currTime) => currTime - 1);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, [analyticsTimeout, close, logoutUrl, pageName, time]);

  useEffect(() => {
    analyticsTimeoutWarning(pageName);
  }, [analyticsTimeoutWarning, pageName]);
  return (
    <div
      className={classnames('modal-session-timeout', commonClasses, classes.dialog)}
      tabIndex="-1"
    >
      <div className={classnames('modal-content', classes.content)}>
        <div className={classnames('modal-header', classes.header)}>
          <h4 className={classnames('modal-title', classes.title)} id="modal-title">
            {title}
          </h4>
        </div>
        <div className={classnames('modal-body', classes.body)} id="modal-body">
          <div className={classnames('modal-content-wrapper', classes.contentWrapper)}>
            <RawHtml type="p">{message}</RawHtml>
            <p
              aria-live={time > 10 ? 'off' : 'polite'}
              aria-atomic={time === 10 ? 'true' : 'false'}
            >
              {timerMessage} <span>{time}</span>
              <span>{' seconds'}</span>
            </p>
          </div>
        </div>
        <div className={classnames('modal-footer', classes.footer)}>
          {buttons.map((b, i) => (
            <Button
              /* eslint-disable-next-line react/no-array-index-key */
              key={`dialog-button-${i}`}
              className={`btn ${b.className} popbox-btn${i}`}
              data-key={i}
              onClick={
                b.variant === 'primary'
                  ? () => {
                      getSession();
                      close();
                    }
                  : () => {
                      close();
                      window.location = logoutUrl;
                    }
              }
              variant={b.variant}
              autofocus={b.variant === 'primary' ? true : undefined}
            >
              {b.text}
              {b.variant === 'primary' && (
                <>
                  <i className="icon-arrow-thin-right" aria-hidden="true" />
                  <Spinner loading={false} />
                </>
              )}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

SessionTimeoutBare.propTypes = {
  analyticsTimeout: PropTypes.func.isRequired,
  analyticsTimeoutWarning: PropTypes.func.isRequired,
  buttons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  close: PropTypes.func.isRequired,
  commonClasses: PropTypes.string.isRequired,
  getSession: PropTypes.func.isRequired,
  logoutUrl: PropTypes.string,
  message: PropTypes.string,
  pageName: PropTypes.string,
  timerMessage: PropTypes.string,
  title: PropTypes.string,
};

SessionTimeoutBare.defaultProps = {
  logoutUrl: 'https://bottomline.com',
  message: '',
  pageName: '',
  timerMessage: '',
  title: '',
};

const mapStateToProps = ({ page, timeOut }) => {
  const buttons = [
    {
      className: 'btn btn-mobile btn-keep-session',
      text: timeOut.keepSessionLabel,
      variant: 'primary',
    },
    {
      className: 'btn btn-mobile btn-mobile--secondary btn-logout',
      text: timeOut.logoutLabel,
      variant: 'secondary',
    },
  ];

  return {
    buttons,
    logoutUrl: timeOut.logoutUrl,
    message: timeOut.message,
    pageName: page.name,
    timerMessage: timeOut.timerMessage,
    title: timeOut.title,
  };
};

const SessionTimeoutStyled = withStyles(styles)(SessionTimeoutBare);

export default connect(mapStateToProps, {
  analyticsTimeout: analyticsActions.oaoTimeout,
  analyticsTimeoutWarning: analyticsActions.oaoTimeoutWarning,
  close: dialogActions.close,
  getSession: sessionActions.getSession,
})(SessionTimeoutStyled);
